/**
 * MyCityStrip — a compact "today at a glance" panel for /local.
 *
 * Combines:
 *   • FoodPulse-flavoured mandi price grid (8 commodities)
 *   • FarmersPulse-style alerts (agri/climate events pulled from NewsPulse)
 *
 * The component fails-open: any HTTP error hides the whole strip so it
 * never leaves an awkward gap on the /local page.
 */
import { useEffect, useState } from "react";
import { AlertTriangle, TrendingUp, IndianRupee, DollarSign, ExternalLink } from "lucide-react";

const CURRENCY_ICON = { INR: IndianRupee, USD: DollarSign };

function fmtPrice(row) {
  const Icon = CURRENCY_ICON[row.currency];
  const price = Number(row.price).toLocaleString(undefined, {
    minimumFractionDigits: row.price % 1 ? 2 : 0,
    maximumFractionDigits: 2,
  });
  return (
    <span className="inline-flex items-center gap-0.5 font-semibold text-slate-900">
      {Icon ? <Icon className="w-3.5 h-3.5" /> : <span>{row.currency}</span>}
      {price}
      <span className="text-slate-400 font-normal ml-0.5 text-[10px] lowercase">
        /{row.unit}
      </span>
    </span>
  );
}

export default function MyCityStrip({ location }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!location) return;
    let alive = true;
    (async () => {
      try {
        const country = (location.country || "IN").toUpperCase();
        const slug = (s) =>
          (s || "")
            .toLowerCase()
            .normalize("NFKD")
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-|-$/g, "");
        const parts = [country, slug(location.admin1), slug(location.city)].filter(Boolean);
        const locationId = parts.length ? parts.join("-") : null;
        const params = new URLSearchParams({ country });
        if (locationId) params.set("location_id", locationId);
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL || ""}/api/newspulse/v1/farmerspulse/city-strip?${params.toString()}`
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (alive) setData(d);
      } catch (e) {
        if (alive) setError(e.message || "load_failed");
      }
    })();
    return () => { alive = false; };
  }, [location]);

  // Fail open — render nothing on error / empty
  if (error || !data) return null;
  const hasPrices = (data.prices || []).length > 0;
  const hasAlerts = (data.alerts || []).length > 0;
  if (!hasPrices && !hasAlerts) return null;

  return (
    <section
      className="mt-4 mb-6 rounded-2xl border border-slate-200 bg-white overflow-hidden"
      aria-label="My City strip"
      data-testid="my-city-strip"
    >
      {/* Mandi price grid */}
      {hasPrices && (
        <div data-testid="my-city-prices">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 bg-slate-50">
            <h2 className="text-sm font-bold text-slate-900 inline-flex items-center gap-1.5">
              <TrendingUp className="w-4 h-4 text-emerald-600" /> Today at the market
            </h2>
            <span
              className="rounded-full bg-amber-100 text-amber-800 border border-amber-200 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5"
              title={data.disclaimer}
            >
              Sample data
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-slate-100 border-b border-slate-100">
            {(data.prices || []).slice(0, 8).map((r) => (
              <div
                key={r.id}
                className="px-3 py-2.5 flex flex-col gap-0.5"
                data-testid={`my-city-price-${r.id}`}
              >
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold truncate">
                  {r.label}
                </span>
                {fmtPrice(r)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Agri / climate alerts */}
      {hasAlerts && (
        <div data-testid="my-city-alerts">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 bg-amber-50/60">
            <h2 className="text-sm font-bold text-amber-900 inline-flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4" /> Alerts nearby
            </h2>
            <span className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">
              FarmersPulse
            </span>
          </div>
          <ul className="divide-y divide-slate-100">
            {(data.alerts || []).slice(0, 4).map((a) => (
              <li key={a.event_id} className="px-4 py-2.5" data-testid={`my-city-alert-${a.event_id}`}>
                <a
                  href={`/events/${a.event_id}`}
                  className="group flex items-start gap-2"
                >
                  <span className="text-[10px] uppercase tracking-widest text-amber-700 font-bold mt-0.5 shrink-0 whitespace-nowrap">
                    {(a.primary_category || "").split("/")[0] || "ALERT"}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-slate-900 group-hover:text-blue-700 line-clamp-2">
                      {a.title}
                    </span>
                    {a.summary && (
                      <span className="block text-xs text-slate-500 line-clamp-1 mt-0.5">
                        {a.summary}
                      </span>
                    )}
                  </span>
                  <ExternalLink className="w-3.5 h-3.5 text-slate-400 group-hover:text-blue-700 shrink-0 mt-1" />
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
